import { z } from 'zod';

/**
 * UniFi Network Integration API v1 payloads (worker presence poll) plus the tolerant
 * prototype payload shape (`clientListFromPayload`, prototype lines 3013-3015).
 */

/** Prototype `normMac(v)` (line 2812): trim, upper-case, dashes → colons; null/undefined → ''. */
export function normalizeUnifiMac(v: unknown): string {
  const s = typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
  return s.trim().toUpperCase().replace(/-/g, ':');
}

/** Same regex the prototype applies in `saveEmployee` (line 3010) and the DB check on employees.mac. */
export const MAC_RE = /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/;

export function isValidMac(mac: string): boolean {
  return MAC_RE.test(mac);
}

export const UNIFI_CLIENT_TYPES = ['WIRED', 'WIRELESS', 'VPN', 'TELEPORT'] as const;
export type UniFiClientType = (typeof UNIFI_CLIENT_TYPES)[number];

/** Default page size used when paginating `GET /sites/{siteId}/clients`. */
export const UNIFI_PAGE_LIMIT = 200;

const nullishString = z.string().nullish();
const epochOrIso = z.union([z.string(), z.number()]).nullish();

/** One client row of the v1 list response. Unknown keys are kept (passthrough). */
export const unifiV1ClientSchema = z
  .object({
    id: z.string().optional(),
    name: nullishString,
    connectedAt: nullishString,
    lastSeen: nullishString,
    ipAddress: nullishString,
    type: nullishString,
    macAddress: z.string().min(1),
    uplinkDeviceId: nullishString,
    access: z.object({ type: z.string() }).passthrough().optional(),
  })
  .passthrough();
export type UniFiV1Client = z.infer<typeof unifiV1ClientSchema>;

/** `GET {base}/proxy/network/integration/v1/sites/{siteId}/clients?offset&limit`. */
export const unifiV1ClientListSchema = z
  .object({
    offset: z.number().int().nonnegative(),
    limit: z.number().int().nonnegative(),
    count: z.number().int().nonnegative(),
    totalCount: z.number().int().nonnegative(),
    data: z.array(unifiV1ClientSchema),
  })
  .passthrough();
export type UniFiV1ClientList = z.infer<typeof unifiV1ClientListSchema>;

/** Field set read by the prototype's `clientMac` / `clientOnline` (lines 3014-3015). */
interface RawClientFields {
  mac?: string | null | undefined;
  macAddress?: string | null | undefined;
  client_mac?: string | null | undefined;
  clientMac?: string | null | undefined;
  id?: string | null | undefined;
  online?: boolean | undefined;
  connected?: boolean | undefined;
  is_wired?: boolean | undefined;
}

/** Prototype `clientMac(c)` (line 3014). */
export function clientMac(c: RawClientFields): string {
  return normalizeUnifiMac(c.mac || c.macAddress || c.client_mac || c.clientMac || c.id);
}

/**
 * Prototype `clientOnline(c)` (line 3015): explicit `online:false`/`connected:false` wins; otherwise any
 * client carrying a MAC is treated as online (the v1 endpoint only lists connected clients).
 */
export function clientOnline(c: RawClientFields): boolean {
  if (c.online === false || c.connected === false) return false;
  return c.online === true || c.connected === true || c.is_wired === false || !!c.mac || !!c.macAddress;
}

/**
 * Tolerant client row: accepts the v1 shape (`macAddress`) and the legacy/prototype shapes
 * (`mac`, `client_mac`, `clientMac`, `id`, `online`, `connected`, `is_wired`, `hostname`, `ip`).
 */
export const unifiAnyClientSchema = z
  .object({
    id: nullishString,
    mac: nullishString,
    macAddress: nullishString,
    client_mac: nullishString,
    clientMac: nullishString,
    name: nullishString,
    hostname: nullishString,
    ip: nullishString,
    ipAddress: nullishString,
    type: nullishString,
    connectedAt: nullishString,
    lastSeen: nullishString,
    first_seen: epochOrIso,
    last_seen: epochOrIso,
    online: z.boolean().optional(),
    connected: z.boolean().optional(),
    is_wired: z.boolean().optional(),
    uplinkDeviceId: nullishString,
  })
  .passthrough()
  .refine((c) => clientMac(c) !== '', { message: 'UniFi client has no MAC address' });
export type UniFiRawClient = z.infer<typeof unifiAnyClientSchema>;

/** Prototype `clientListFromPayload(data)` (line 3013): array | {clients:[…]} | {data:[…]} | []. */
export function clientListFromPayload(data: unknown): unknown[] {
  if (Array.isArray(data)) return data as unknown[];
  if (typeof data === 'object' && data !== null) {
    const rec = data as Record<string, unknown>;
    if (Array.isArray(rec.clients)) return rec.clients as unknown[];
    if (Array.isArray(rec.data)) return rec.data as unknown[];
  }
  return [];
}

const clientArray = z.array(unifiAnyClientSchema);

/** Every payload shape `clientListFromPayload` understands; anything else is rejected (never coerced). */
export const unifiClientPayloadSchema = z.union([
  clientArray,
  z.object({ clients: clientArray }).passthrough(),
  z.object({ data: clientArray }).passthrough(),
]);
export type UniFiClientPayload = z.infer<typeof unifiClientPayloadSchema>;

export interface UniFiClientRecord {
  /** Normalized `AA:BB:CC:DD:EE:FF` (prototype normMac). */
  mac: string;
  name: string;
  ip: string;
  type: string;
  connectedAt: string | null;
  online: boolean;
  raw: Record<string, unknown>;
}

export const unifiClientRecordSchema = z.object({
  mac: z.string().min(1),
  name: z.string(),
  ip: z.string(),
  type: z.string(),
  connectedAt: z.string().nullable(),
  online: z.boolean(),
  raw: z.record(z.unknown()),
});

function isoFromEpochOrIso(v: string | number | null | undefined): string | null {
  if (typeof v === 'number') return new Date(v * 1000).toISOString();
  if (typeof v === 'string' && v.trim() !== '') return v;
  return null;
}

export function toUniFiClientRecord(raw: UniFiRawClient): UniFiClientRecord {
  const type = raw.type ?? (raw.is_wired === true ? 'WIRED' : raw.is_wired === false ? 'WIRELESS' : '');
  return {
    mac: clientMac(raw),
    name: (raw.name ?? raw.hostname ?? '').trim(),
    ip: (raw.ipAddress ?? raw.ip ?? '').trim(),
    type,
    connectedAt: raw.connectedAt ?? isoFromEpochOrIso(raw.first_seen),
    online: clientOnline(raw),
    raw: { ...raw },
  };
}

function listOf(parsed: UniFiClientPayload): UniFiRawClient[] {
  if (Array.isArray(parsed)) return parsed;
  const rec = parsed as { clients?: UniFiRawClient[]; data?: UniFiRawClient[] };
  return rec.clients ?? rec.data ?? [];
}

/** Validates a payload of any accepted shape and normalizes every client. Throws `ZodError` on garbage. */
export function parseUniFiClients(payload: unknown): UniFiClientRecord[] {
  return listOf(unifiClientPayloadSchema.parse(payload)).map(toUniFiClientRecord);
}

export function safeParseUniFiClients(payload: unknown): z.SafeParseReturnType<unknown, UniFiClientRecord[]> {
  const parsed = unifiClientPayloadSchema.safeParse(payload);
  if (!parsed.success) return parsed;
  return { success: true, data: listOf(parsed.data).map(toUniFiClientRecord) };
}

/** Set of normalized MACs currently online — the prototype's `active` set in `updateAttendanceFromClients` (3016). */
export function onlineMacs(clients: readonly UniFiClientRecord[]): Set<string> {
  return new Set(clients.filter((c) => c.online).map((c) => c.mac));
}
